import { Popover, Text, WithPopover } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  DATE_TIME_SHORT,
  formatCurrencyValue,
  formatDateTime,
} from '@vend/utilities'
import React from 'react'
import { getPaymentsTotal } from '../../../functions/sale'
import { Payment } from '../../../models/payment'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'

interface PaymentsRowProps {
  payments: Payment[]
}

interface PaymentsPopoverProps {
  payments: Payment[]
  onClose: () => void
}

const PaymentsPopover: React.FC<PaymentsPopoverProps> = ({
  payments,
  onClose,
}) => {
  const t = useT()
  const retailer = useRetailerSettingsManager().get()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    ...DATE_TIME_SHORT,
    timeZone: currentOutlet.time_zone,
  }

  return (
    <Popover className="sale-popover" onClose={onClose}>
      <h1 className="vd-text-label">
        {t('Payments', { _context: 'Sale payments popover title' })}
      </h1>
      <hr className="vd-hr vd-mb0" />
      <div>
        {payments.map(payment => (
          <div key={payment.localId} className="wr-sale-popover-item">
            <div className="vd-flex vd-flex--column">
              <Text className="vd-mb1 pro-payment-name">
                {payment.paymentType.name}
              </Text>
              <Text type="supplementary" className="vd-text--sub">
                {formatDateTime(payment.paymentDate, dateFormatOptions)}
              </Text>
            </div>

            <Text className="pro-payment-amount">
              {formatCurrencyValue(
                payment.amount,
                retailer.culture,
                currentOutlet.currency,
                currentOutlet.currency_symbol
              )}
            </Text>
          </div>
        ))}
      </div>
    </Popover>
  )
}

export const PaymentsRow: React.FC<PaymentsRowProps> = ({ payments }) => {
  const t = useT()
  const retailer = useRetailerSettingsManager().get()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const paymentsCount = payments.length
  const paymentsTotal = getPaymentsTotal(payments)

  const [displayPaymentsPopover, setDisplayPaymentsPopover] = React.useState(
    false
  )

  return (
    <>
      <div>
        <WithPopover direction="left">
          <div>
            <Text
              type="label"
              className="vd-link"
              data-testid="payments-popover-link"
              onClick={() => setDisplayPaymentsPopover(true)}
            >
              {t('Payments', { _context: 'Sale footer payments label' })}
            </Text>
            <Text className="sale-total-line-item--sub pro-number-of-payments">
              {t(
                '{paymentsCount, plural, one {{paymentsCount} Payment made} other {{paymentsCount} Payments made}}',
                { paymentsCount, _context: 'Sale footer payments count' }
              )}
            </Text>
          </div>

          {displayPaymentsPopover && (
            <PaymentsPopover
              payments={payments}
              onClose={() => setDisplayPaymentsPopover(false)}
            />
          )}
        </WithPopover>
      </div>

      <Text type="label" className="sale-total-line-item--value">
        {formatCurrencyValue(
          paymentsTotal,
          retailer.culture,
          currentOutlet.currency,
          currentOutlet.currency_symbol
        )}
      </Text>
    </>
  )
}
